export const statusMap = {
  New: { name: "Nowy", color: "#4299E1" },
  Replied: { name: "Odpowiedziano", color: "#48BB78" },
  AwaitingReply: { name: "Oczekuje", color: "#ED8936" },
  TakingTooLong: { name: "Zbyt długo", color: "#F56565" },
  ClosedSuccess: { name: "Rozwiązany", color: "#A0AEC0" },
  ClosedFail: { name: "Nierozwiązany", color: "#E53E3E" },
  Reopened: { name: "Otwarte ponownie", color: "#8c2ef7ff" },
};

export const defaultStatusInfo = { name: "Nieznany", color: "#A0AEC0" };

/**
 * [2026-10-01, tablica „Wsparcie <moduł>”] Czy zgłoszenie jest ZAMKNIĘTE. Status przychodzi nazwą enuma („ClosedSuccess”,
 * „ClosedFail”) albo liczbą (4, 5 — także jako napis). Jedno miejsce dla nowych miejsc (kalendarz: przeciąganie terminu
 * i okno potwierdzenia); starsze kopie tego porównania (pulpit, okna zgłoszenia, pasek czasu) czekają na przepięcie.
 */
export const zgloszenieZamkniete = (status) => {
  if (status == null || status === "") return false;
  const s = String(status).trim();
  return s === "ClosedSuccess" || s === "ClosedFail" || s === "4" || s === "5";
};

export const TICKET_PRIORITY = {
  LOW: 0,
  MEDIUM: 1,
  HIGH: 2,
  CRITICAL: 3,
};

export const TICKET_TYPE = {
  TECHNICAL: 0,
  DRIVE_ACCESS: 1,
  EQUIPMENT_BUYOUT: 2,
  EQUIPMENT_REQUEST: 3,
  PERSONAL_DATA_CHANGE: 4,
  SAP_SUPPORT: 5,
};

export const TICKET_TYPE_LABELS = {
  [TICKET_TYPE.TECHNICAL]: "Zgłoszenie techniczne",
  [TICKET_TYPE.DRIVE_ACCESS]: "Zmiana uprawnień do dysków sieciowych",
  [TICKET_TYPE.EQUIPMENT_BUYOUT]: "Wykup sprzętu",
  [TICKET_TYPE.EQUIPMENT_REQUEST]: "Zapotrzebowanie na sprzęt IT",
  [TICKET_TYPE.PERSONAL_DATA_CHANGE]: "Zmiana danych personalnych",
  [TICKET_TYPE.SAP_SUPPORT]: "Wsparcie SAP",
};

// Maps frontend TICKET_TYPE to backend TicketType enum values
export const BACKEND_TICKET_TYPE_MAP = {
  [TICKET_TYPE.TECHNICAL]: 1,
  [TICKET_TYPE.DRIVE_ACCESS]: 2,
  [TICKET_TYPE.EQUIPMENT_BUYOUT]: 3,
  [TICKET_TYPE.EQUIPMENT_REQUEST]: 4,
  [TICKET_TYPE.PERSONAL_DATA_CHANGE]: 5,
  [TICKET_TYPE.SAP_SUPPORT]: 7,
};

// Backend TicketType enum value for SAP
export const BACKEND_TYPE_SAP = 7;

// Backend type → short label + color (for ticket list cards)
export const BACKEND_TYPE_INFO = {
  1: { label: "Techniczne", color: "#4299E1" },
  2: { label: "Dyski sieciowe", color: "#805AD5" },
  3: { label: "Wykup sprzętu", color: "#ED8936" },
  4: { label: "Zapotrzebowanie IT", color: "#38A169" },
  5: { label: "Dane personalne", color: "#ED64A6" },
  6: { label: "Dane firmowe", color: "#D69E2E" },
  7: { label: "Wsparcie SAP", color: "#D97706" },
};

export const getBackendTypeInfo = (backendType) =>
  BACKEND_TYPE_INFO[backendType] || { label: "Inne", color: "#A0AEC0" };

export const PRIORITY_INFO = {
  Low: { label: "Niski", color: "#A0AEC0" },
  Normal: { label: "Normalny", color: "#4299E1" },
  High: { label: "Wysoki", color: "#ED8936" },
  Critical: { label: "Krytyczny", color: "#E53E3E" },
};

/* ════════════════════════════════════════════════════════════════════════
   [IT-02 / ZG-09 2026-09-03] JEDNO ŹRÓDŁO NORMALIZACJI PRIORYTETU.

   CO BYŁO ŹLE: `PRIORITY_INFO` ma wyłącznie klucze kanoniczne (Low/Normal/High/Critical),
   a w bazie priorytety kanoniczne NIE SĄ: dane historyczne mają warianty polskie
   („Krytyczny", „Wysoki") i numeryczne („1", „2"), a seeder onboardingu zapisuje „Standard".
   Landing IT robił cichy fallback na „Normalny", lista zgłoszeń w ogóle nie rysowała badge'a
   (mimo komentarza „ALWAYS visible"), a karta zgłoszenia miała WŁASNĄ, trzecią tablicę aliasów.
   SKUTEK DLA UŻYTKOWNIKA: zgłoszenie KRYTYCZNE wyglądało w kolejce jak zwykłe (niebieski badge
   „Normalny") albo nie miało badge'a wcale — 79 z 792 zgłoszeń produkcyjnych, w tym 10
   krytycznych. Agent przeglądający kolejkę wzrokiem nie miał szans zobaczyć spraw pilnych,
   a sortowanie serwerowe (sort=priority) rozumiało te warianty — więc backend i UI mówiły
   co innego o tym samym zgłoszeniu.

   ⚠️ Skala numeryczna jest ROSNĄCA (0 = Niski … 3 = Krytyczny) — dokładnie tak, jak czyta ją
   sortowanie serwerowe w TicketsController.GetAll (sort=priority). Odwrócenie tej mapy
   ustawiłoby sprawy krytyczne na końcu kolejki.
   ════════════════════════════════════════════════════════════════════════ */
export const PRIORITY_ALIASES = {
  low: "Low", niski: "Low", "0": "Low",
  normal: "Normal", normalny: "Normal", medium: "Normal", "średni": "Normal",
  sredni: "Normal", standard: "Normal", "1": "Normal",
  high: "High", wysoki: "High", "2": "High",
  critical: "Critical", krytyczny: "Critical", "3": "Critical",
};

/** Dowolny zapis priorytetu → klucz kanoniczny PRIORITY_INFO. Pusty/nieznany → "Normal". */
export const normalizePriority = (priority) =>
  PRIORITY_ALIASES[String(priority ?? "").trim().toLowerCase()] || "Normal";

/** Priorytet do wyświetlenia (etykieta + kolor) — ZAWSZE zwraca wpis, nigdy null. */
export const getPriorityInfo = (priority) => PRIORITY_INFO[normalizePriority(priority)];

// Category name → quick filter tile mapping
// Used to count tickets per quick filter based on their category
export const CATEGORY_TO_QUICKFILTER = {
  "Problem z komputerem": "techniczne",
  "Usterka sieciowa": "techniczne",
  "Reset hasła dostępu": "techniczne",
  "Problem z drukarką": "techniczne",
  "Zgłoszenie SAP": "sap",
  "Problem z telefonem": "techniczne",
  "Dostęp do dysków sieciowych": "dyski",
  "Szkolenie IT": "hr",
  "Zmiana uprawnień użytkownika": "hr",
  "Zapotrzebowanie IT": "zakupy",
  "Onboarding HR": "hr",
  "Zablokowanie użytkownika": "hr",
  "Offboarding": "hr",
};
